import React, { useEffect, useState } from "react";
import { Button, Card, Col, Row, Spinner, Alert, Table, Badge, Modal, Form, InputGroup, Pagination } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import ReactApexChart from "react-apexcharts";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency, formatNumber, formatDate } from "../utils/formatters";
import './FacturesImpayees.css';

export default function FacturesImpayees() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedFacture, setSelectedFacture] = useState(null);
  const [showPaiementModal, setShowPaiementModal] = useState(false);
  const [showHistoriqueModal, setShowHistoriqueModal] = useState(false);

  // États pour les notifications
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationTitle, setNotificationTitle] = useState('');

  // États pour la relance
  const [showRelanceModal, setShowRelanceModal] = useState(false);
  const [sendingRelance, setSendingRelance] = useState(false);
  const [relanceData, setRelanceData] = useState({
    type: 'standard',
    message_personnalise: ''
  });

  // États pour la pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  const [paiementData, setPaiementData] = useState({
    montant: '',
    mode_reglement: 'especes',
    reference: '',
    date_reglement: new Date().toISOString().split('T')[0]
  });
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(true);

  useEffect(() => {
    fetchFacturesImpayees();
    
    let interval;
    if (autoRefresh) {
      interval = setInterval(fetchFacturesImpayees, 60000);
    }
    return () => clearInterval(interval);
  }, [autoRefresh]);

  // Fonctions pour afficher les notifications
  const showSuccess = (message) => {
    setNotificationTitle('Succès');
    setNotificationMessage(message);
    setShowSuccessNotification(true);
    setTimeout(() => setShowSuccessNotification(false), 4000);
  };

  const showError = (message) => {
    setNotificationTitle('Erreur');
    setNotificationMessage(message);
    setShowErrorNotification(true);
    setTimeout(() => setShowErrorNotification(false), 4000);
  };

  const fetchFacturesImpayees = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      
      const response = await fetch(
        `https://sawd.julesferry-ga.com/api/factures_impayees.php`,
        {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          navigate("/pages/signin2");
          throw new Error("Session expirée");
        }
        throw new Error(`Erreur HTTP: ${response.status}`);
      }
      
      const result = await response.json();
      setData(result);
      setCurrentPage(1); // Reset à la première page après chargement
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleViewHistorique = async (facture) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `https://sawd.julesferry-ga.com/api/factures_impayees.php?action=paiements&id=${facture.id_facture}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      const result = await response.json();
      setSelectedFacture({ ...facture, paiements: result.paiements });
      setShowHistoriqueModal(true);
    } catch (err) {
      showError("Erreur lors du chargement de l'historique");
    }
  };

  const handleSaisirPaiement = (facture) => {
    setSelectedFacture(facture);
    setPaiementData({
      ...paiementData,
      montant: facture.reste_a_payer,
      date_reglement: new Date().toISOString().split('T')[0]
    });
    setShowPaiementModal(true);
  };

  // ✅ VERSION CORRIGÉE : Envoi de l'ID utilisateur avec notifications
  const handlePaiementSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem("token");
      
      const userStr = localStorage.getItem("user");
      const user = userStr ? JSON.parse(userStr) : null;
      
      if (!user || !user.id) {
        showError("Utilisateur non identifié");
        return;
      }

      console.log("💰 Envoi paiement:", {
        facture: selectedFacture.numero_facture,
        montant: paiementData.montant,
        mode: paiementData.mode_reglement,
        utilisateur: user.id
      });

      const response = await fetch(
        `https://sawd.julesferry-ga.com/api/factures.php`,
        {
          method: 'POST',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            action: 'encaisser',
            id_facture: selectedFacture.id_facture,
            montant: parseFloat(paiementData.montant),
            mode_reglement: paiementData.mode_reglement,
            reference: paiementData.reference,
            date_reglement: paiementData.date_reglement,
            id_utilisateur: user.id
          })
        }
      );
      
      if (response.ok) {
        setShowPaiementModal(false);
        fetchFacturesImpayees();
        showSuccess("✅ Paiement enregistré avec succès");
        
        // Réinitialiser le formulaire
        setPaiementData({
          montant: '',
          mode_reglement: 'especes',
          reference: '',
          date_reglement: new Date().toISOString().split('T')[0]
        });
      } else {
        const error = await response.json();
        showError("❌ " + (error.message || "Échec de l'enregistrement"));
      }
    } catch (err) {
      console.error("Erreur paiement:", err);
      showError("❌ Erreur de connexion au serveur");
    }
  };

  // ✅ Fonction améliorée pour envoyer une relance avec notifications
  const handleEnvoyerRelance = async (facture) => {
    setSelectedFacture(facture);
    setShowRelanceModal(true);
  };

  // ✅ Nouvelle fonction pour confirmer l'envoi de la relance avec notifications
  const handleConfirmerRelance = async () => {
    if (!selectedFacture) return;
    
    setSendingRelance(true);
    
    try {
      const token = localStorage.getItem("token");
      const userStr = localStorage.getItem("user");
      const user = userStr ? JSON.parse(userStr) : null;
      
      if (!user || !user.id) {
        showError("❌ Utilisateur non identifié");
        setSendingRelance(false);
        return;
      }

      console.log("📧 Envoi relance pour facture:", selectedFacture.numero_facture);

      const response = await fetch(
        `https://sawd.julesferry-ga.com/api/relances.php`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            id_facture: selectedFacture.id_facture,
            id_utilisateur: user.id,
            type_relance: relanceData.type,
            message: relanceData.message_personnalise
          })
        }
      );

      const result = await response.json();

      if (result.success) {
        setShowRelanceModal(false);
        showSuccess(`✅ Relance envoyée avec succès`);
        
        setRelanceData({
          type: 'standard',
          message_personnalise: ''
        });
      } else {
        showError("❌ " + (result.message || "Échec de l'envoi"));
      }
    } catch (err) {
      console.error("Erreur relance:", err);
      showError("❌ Erreur de connexion au serveur");
    } finally {
      setSendingRelance(false);
    }
  };

  const getPrioriteBadge = (priorite, jours) => {
    const badges = {
      'expiree': { bg: 'danger', text: `Exp. (${jours}j)`, icon: 'ri-alert-line' },
      'urgente': { bg: 'warning', text: `Urgente (J-${jours})`, icon: 'ri-alarm-warning-line' },
      'normale': { bg: 'success', text: `Dans ${jours}j`, icon: 'ri-check-line' }
    };
    return badges[priorite] || badges.normale;
  };

  const getEtatPaiementBadge = (etat) => {
    const badges = {
      'impayee': { bg: 'danger', text: 'Impayée' },
      'partielle': { bg: 'warning', text: 'Partielle' },
      'payee': { bg: 'success', text: 'Payée' }
    };
    return badges[etat] || { bg: 'secondary', text: etat };
  };

  const getModePaiementIcon = (mode) => {
    const icons = {
      'especes': 'ri-money-cny-circle-line',
      'cheque': 'ri-bank-line',
      'virement': 'ri-bank-card-line',
      'carte_bancaire': 'ri-bank-card-2-line',
      'traite': 'ri-file-copy-line'
    };
    return icons[mode] || 'ri-bank-line';
  };

  // Filtrer les factures
  const filteredFactures = data?.factures?.filter(facture => {
    const matchesSearch = facture.numero_facture.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         facture.client.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         facture.code_client.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filter === 'all' || facture.etat_paiement === filter;
    return matchesSearch && matchesFilter;
  }) || [];

  // ✅ PAGINATION
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredFactures.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredFactures.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const renderPagination = () => {
    let items = [];
    const maxVisible = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);

    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    if (startPage > 1) {
      items.push(
        <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
          1
        </Pagination.Item>
      );
      if (startPage > 2) {
        items.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
      }
    }

    for (let number = startPage; number <= endPage; number++) {
      items.push(
        <Pagination.Item 
          key={number} 
          active={number === currentPage}
          onClick={() => handlePageChange(number)}
        >
          {number}
        </Pagination.Item>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        items.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
      }
      items.push(
        <Pagination.Item key={totalPages} onClick={() => handlePageChange(totalPages)}>
          {totalPages}
        </Pagination.Item>
      );
    }

    return items;
  };

  // Configuration des graphiques
  const seriesEvolution = [{
    name: 'Montant impayé',
    data: data?.evolution?.map(item => item.montant_impaye) || []
  }];

  const optionsEvolution = {
    chart: {
      type: 'area',
      height: 300,
      toolbar: { show: true }
    },
    colors: ['#ef4444'],
    fill: {
      type: 'gradient',
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.7,
        opacityTo: 0.2
      }
    },
    xaxis: {
      categories: data?.evolution?.map(item => {
        const [year, month] = item.periode.split('-');
        return `${month}/${year}`;
      }) || [],
      title: { text: 'Période' }
    },
    yaxis: {
      title: { text: 'Montant (FCFA)' },
      labels: { formatter: (value) => formatCurrency(value) }
    },
    tooltip: {
      y: { formatter: (value) => formatCurrency(value) }
    }
  };

  if (loading && !data) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <Spinner animation="border" variant="danger" />
        <span className="ms-3">Chargement des factures impayées...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <Alert variant="danger">
          <Alert.Heading>Erreur</Alert.Heading>
          <p>{error}</p>
          <Button variant="outline-danger" onClick={fetchFacturesImpayees}>
            Réessayer
          </Button>
        </Alert>
      </div>
    );
  }

  return (
    <React.Fragment>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        {/* ✅ NOTIFICATIONS - Placées ici pour être visibles */}
        <div className="notification-container">
          <div className={`notification notification-success ${showSuccessNotification ? 'show' : ''}`}>
            <div className="notification-icon">
              <i className="ri-checkbox-circle-line"></i>
            </div>
            <div className="notification-content">
              <h6 className="notification-title">{notificationTitle}</h6>
              <p className="notification-message">{notificationMessage}</p>
            </div>
            <button 
              className="notification-close"
              onClick={() => setShowSuccessNotification(false)}
            >
              <i className="ri-close-line"></i>
            </button>
            <div className="notification-progress"></div>
          </div>

          <div className={`notification notification-error ${showErrorNotification ? 'show' : ''}`}>
            <div className="notification-icon">
              <i className="ri-error-warning-line"></i>
            </div>
            <div className="notification-content">
              <h6 className="notification-title">{notificationTitle}</h6>
              <p className="notification-message">{notificationMessage}</p>
            </div>
            <button 
              className="notification-close"
              onClick={() => setShowErrorNotification(false)}
            >
              <i className="ri-close-line"></i>
            </button>
            <div className="notification-progress"></div>
          </div>
        </div>

        {/* En-tête */}
        <div className="d-md-flex align-items-center justify-content-between mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item"><Link to="/dashboard">Tableau de bord</Link></li>
              <li className="breadcrumb-item active">Factures impayées</li>
            </ol>
            <h4 className="main-title mb-0 d-flex align-items-center">
              <i className="ri-error-warning-line text-danger me-2"></i>
              Gestion des impayés
              <Badge bg="danger" className="ms-3">
                {data?.statistiques?.nb_factures_impayees || 0} facture(s)
              </Badge>
            </h4>
          </div>
          <div className="d-flex gap-2 mt-3 mt-md-0">
            <div className="form-check form-switch me-3 d-flex align-items-center">
              <input
                className="form-check-input"
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                id="autoRefresh"
              />
              <label className="form-check-label small" htmlFor="autoRefresh">
                Auto-refresh
              </label>
            </div>
            <Button variant="outline-primary" onClick={fetchFacturesImpayees}>
              <i className="ri-refresh-line"></i> Actualiser
            </Button>
          </div>
        </div>

        {/* KPI Cards */}
         {/* KPI Cards */}
        <Row className="g-3 mb-4">
          <Col sm="6" xl="3">
            <Card className="card-one h-100 border-0 shadow-sm border-start border-danger border-4">
              <Card.Body>
                <div className="d-flex align-items-center">
                  <div className="kpi-icon bg-danger-soft rounded-circle p-3 me-3">
                    <i className="ri-error-warning-line fs-24 text-danger"></i>
                  </div>
                  <div>
                    <h6 className="text-secondary mb-1">Total impayés</h6>
                    <h3 className="mb-0 fw-bold text-danger">{formatCurrency(data?.statistiques?.total_impaye || 0)}</h3>
                    <small className="text-secondary">{data?.statistiques?.nb_factures_impayees || 0} factures</small>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col sm="6" xl="3">
            <Card className="card-one h-100 border-0 shadow-sm border-start border-warning border-4">
              <Card.Body>
                <div className="d-flex align-items-center">
                  <div className="kpi-icon bg-warning-soft rounded-circle p-3 me-3">
                    <i className="ri-timer-line fs-24 text-warning"></i>
                  </div>
                  <div>
                    <h6 className="text-secondary mb-1">Montant expiré</h6>
                    <h3 className="mb-0 fw-bold text-warning">{formatCurrency(data?.statistiques?.total_expire || 0)}</h3>
                    <small className="text-danger">Retard max: {data?.statistiques?.retard_max_jours || 0}j</small>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col sm="6" xl="3">
            <Card className="card-one h-100 border-0 shadow-sm">
              <Card.Body>
                <div className="d-flex align-items-center">
                  <div className="kpi-icon bg-info-soft rounded-circle p-3 me-3">
                    <i className="ri-group-line fs-24 text-info"></i>
                  </div>
                  <div>
                    <h6 className="text-secondary mb-1">Clients concernés</h6>
                    <h3 className="mb-0 fw-bold">{data?.statistiques?.nb_clients_concernes || 0}</h3>
                    <small className="text-secondary">Montant moyen: {formatCurrency(data?.statistiques?.montant_moyen || 0)}</small>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col sm="6" xl="3">
            <Card className="card-one h-100 border-0 shadow-sm">
              <Card.Body>
                <div className="d-flex align-items-center">
                  <div className="kpi-icon bg-success-soft rounded-circle p-3 me-3">
                    <i className="ri-calendar-check-line fs-24 text-success"></i>
                  </div>
                  <div>
                    <h6 className="text-secondary mb-1">À encaisser (30j)</h6>
                    <h3 className="mb-0 fw-bold text-success">{formatCurrency(data?.previsions?.trente_jours || 0)}</h3>
                    <small className="text-secondary">{data?.previsions?.nb_factures || 0} factures</small>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Graphiques */}
        <Row className="g-3 mb-4">
          <Col xl="8">
            <Card className="border-0 shadow-sm">
              <Card.Header className="bg-transparent border-0">
                <Card.Title as="h6">Évolution des impayés (12 mois)</Card.Title>
              </Card.Header>
              <Card.Body>
                <ReactApexChart 
                  series={seriesEvolution} 
                  options={optionsEvolution} 
                  type="area" 
                  height={300} 
                />
              </Card.Body>
            </Card>
          </Col>

          <Col xl="4">
            <Card className="border-0 shadow-sm h-100">
              <Card.Header className="bg-transparent border-0">
                <Card.Title as="h6">Top clients débiteurs</Card.Title>
              </Card.Header>
              <Card.Body>
                <div className="table-responsive">
                  <Table size="sm" className="align-middle">
                    <tbody>
                      {data?.clients?.slice(0, 5).map((client, idx) => (
                        <tr key={client.id_client}>
                          <td className="text-muted">{idx + 1}</td>
                          <td>
                            <div>
                              <span className="d-block fw-semibold small">{client.raison_sociale}</span>
                              <small className="text-secondary">{client.code_client}</small>
                            </div>
                          </td>
                          <td className="text-end">
                            <span className="fw-bold text-danger small">{formatCurrency(client.total_du)}</span>
                            <br />
                            <small className="text-secondary">{client.nb_factures_impayees} facture(s)</small>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
                <hr />
                <div className="d-flex justify-content-between align-items-center">
                  <span className="small text-secondary">Paiements partiels</span>
                  <Badge bg="warning">{data?.statistiques?.nb_paiements_partiels || 0}</Badge>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Filtres */}
        <Card className="border-0 shadow-sm mb-4">
          <Card.Body>
            <Row className="g-3">
              <Col md="6">
                <InputGroup>
                  <InputGroup.Text>
                    <i className="ri-search-line"></i>
                  </InputGroup.Text>
                  <Form.Control
                    placeholder="Rechercher facture ou client..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </InputGroup>
              </Col>
              <Col md="4">
                <Form.Select value={filter} onChange={(e) => setFilter(e.target.value)}>
                  <option value="all">Tous les états</option>
                  <option value="impayee">Impayées</option>
                  <option value="partielle">Paiements partiels</option>
                </Form.Select>
              </Col>
              <Col md="2">
                <div className="text-end text-secondary">
                  <small>{filteredFactures.length} facture(s)</small>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Tableau des factures impayées avec pagination */}
        <Card className="border-0 shadow-sm">
          <Card.Body className="p-0">
            <div className="table-responsive">
              <Table hover className="align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Facture</th>
                    <th>Date</th>
                    <th>Client</th>
                    <th>Échéance</th>
                    <th className="text-end">Montant TTC</th>
                    <th className="text-end">Réglé</th>
                    <th className="text-end">Reste</th>
                    <th>Retard</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredFactures.length > 0 ? (
                    filteredFactures.map((facture) => {
                      const priorite = getPrioriteBadge(facture.priorite, facture.jours_retard || Math.abs(facture.jours_avant_echeance));
                      
                      return (
                        <tr key={facture.id_facture} className={facture.priorite === 'expiree' ? 'table-danger' : ''}>
                          <td>
                            <Link to={`/ventes/factures/${facture.id_facture}`} className="fw-semibold">
                              {facture.numero_facture}
                            </Link>
                          </td>
                          <td>{formatDate(facture.date_facture)}</td>
                          <td>
                            <div>
                              <span className="d-block fw-semibold">{facture.client}</span>
                              <small className="text-secondary">{facture.code_client}</small>
                            </div>
                          </td>
                          <td>
                            <div>
                              <span className="d-block">{formatDate(facture.date_echeance)}</span>
                              <small className="text-secondary">
                                {facture.jours_avant_echeance > 0 
                                  ? `J-${facture.jours_avant_echeance}`
                                  : `J+${Math.abs(facture.jours_retard)}`
                                }
                              </small>
                            </div>
                          </td>
                          <td className="text-end fw-bold">{formatCurrency(facture.montant_ttc)}</td>
                          <td className="text-end text-success">{formatCurrency(facture.montant_regle)}</td>
                          <td className="text-end text-danger fw-bold">{formatCurrency(facture.reste_a_payer)}</td>
                          <td>
                            <Badge bg={priorite.bg} className="d-flex align-items-center gap-1" style={{ width: 'fit-content' }}>
                              <i className={priorite.icon}></i>
                              {priorite.text}
                            </Badge>
                          </td>
                          <td>
                            <div className="d-flex gap-2 justify-content-center">
                              <Button 
                                variant="outline-primary" 
                                size="sm" 
                                className="action-btn rounded-circle"
                                onClick={() => handleViewHistorique(facture)}
                                title="Historique des paiements"
                                style={{ width: '36px', height: '36px' }}
                              >
                                <i className="ri-history-line"></i>
                              </Button>
                              <Button 
                                variant="outline-success" 
                                size="sm" 
                                className="action-btn rounded-circle"
                                onClick={() => handleSaisirPaiement(facture)}
                                title="Saisir un paiement"
                                style={{ width: '36px', height: '36px' }}
                              >
                                <i className="ri-bank-card-line"></i>
                              </Button>
                              <Button 
                                variant="outline-warning" 
                                size="sm" 
                                className="action-btn rounded-circle"
                                onClick={() => handleEnvoyerRelance(facture)}
                                title="Envoyer une relance"
                                style={{ width: '36px', height: '36px' }}
                              >
                                <i className="ri-mail-send-line"></i>
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="9" className="text-center py-5">
                        <i className="ri-checkbox-circle-line fs-48 text-success mb-3 d-block"></i>
                        <h5>Aucune facture impayée</h5>
                        <p className="text-secondary mb-0">Toutes les factures sont soldées</p>
                      </td>
                    </tr>
                  )}
                </tbody>
                <tfoot className="table-light fw-bold">
                  <tr>
                    <td colSpan="6" className="text-end">TOTAUX:</td>
                    <td className="text-end text-danger">
                      {formatCurrency(filteredFactures.reduce((acc, f) => acc + f.reste_a_payer, 0))}
                    </td>
                    <td colSpan="2"></td>
                  </tr>
                </tfoot>
              </Table>
            </div>
          </Card.Body>
        </Card>

        <Footer />
      </div>

      {/* Vos modales (inchangées) */}
      {/* ... */}

            <Modal show={showPaiementModal} onHide={() => setShowPaiementModal(false)} centered className="modern-modal">
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="w-100">
            <div className="text-center">
              <div className="modal-icon mb-3">
                <div className="icon-wrapper bg-success-soft">
                  <i className="ri-bank-card-line text-success" style={{ fontSize: '2.5rem' }}></i>
                </div>
              </div>
              <h3 className="fw-bold text-success">Enregistrer un paiement</h3>
              <p className="text-white small">Saisissez les informations du règlement</p>
            </div>
          </Modal.Title>
        </Modal.Header>
        
        <Form onSubmit={handlePaiementSubmit}>
          <Modal.Body className="pt-2 px-4">
            {selectedFacture && (
              <>
                {/* Alerte facture améliorée */}
                <Alert variant="info" className="mb-4 p-3" style={{
                  borderRadius: '16px',
                  background: 'linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%)',
                  border: 'none',
                  borderLeft: '4px solid #0ea5e9'
                }}>
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center gap-3">
                      <div className="bg-white p-2 rounded-circle shadow-sm">
                        <i className="ri-file-list-3-line text-info"></i>
                      </div>
                      <div>
                        <div className="fw-bold">{selectedFacture.numero_facture}</div>
                        <div className="small">
                          <i className="ri-user-line me-1"></i>
                          {selectedFacture.client}
                        </div>
                      </div>
                    </div>
                    <div className="text-end">
                      <span className="badge bg-danger px-3 py-2 rounded-pill shadow-sm">
                        <i className="ri-money-euro-circle-line me-2"></i>
                        Reste: {formatCurrency(selectedFacture.reste_a_payer)}
                      </span>
                    </div>
                  </div>
                </Alert>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">
                        <i className="ri-calendar-line me-2 text-primary"></i>
                        Date de paiement
                      </Form.Label>
                      <Form.Control
                        type="date"
                        value={paiementData.date_reglement}
                        onChange={(e) => setPaiementData({...paiementData, date_reglement: e.target.value})}
                        required
                        className="form-control-modern"
                      />
                    </Form.Group>
                  </Col>
                  
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">
                        <i className="ri-money-euro-circle-line me-2 text-primary"></i>
                        Montant
                      </Form.Label>
                      <InputGroup>
                        <Form.Control
                          type="number"
                          step="0.01"
                          min="0.01"
                          max={selectedFacture.reste_a_payer}
                          value={paiementData.montant}
                          onChange={(e) => setPaiementData({...paiementData, montant: parseFloat(e.target.value)})}
                          required
                          className="form-control-modern"
                          placeholder="0.00"
                        />
                        <InputGroup.Text className="bg-light border">FCFA</InputGroup.Text>
                      </InputGroup>
                      <Form.Text className="text-muted">
                        Max: {formatCurrency(selectedFacture.reste_a_payer)}
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">
                    <i className="ri-bank-card-line me-2 text-primary"></i>
                    Mode de règlement
                  </Form.Label>
                  <Form.Select
                    value={paiementData.mode_reglement}
                    onChange={(e) => setPaiementData({...paiementData, mode_reglement: e.target.value})}
                    required
                    className="form-select-modern py-2"
                  >
                    <option value="especes">Espèces</option>
                    <option value="cheque">Chèque</option>
                    <option value="virement">Virement</option>
                    <option value="carte_bancaire">Carte bancaire</option>
                    <option value="traite">Traite</option>
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">
                    <i className="ri-file-text-line me-2 text-primary"></i>
                    Référence <span className="text-muted fw-normal">(optionnel)</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="N° chèque, référence virement..."
                    value={paiementData.reference}
                    onChange={(e) => setPaiementData({...paiementData, reference: e.target.value})}
                    className="form-control-modern"
                  />
                </Form.Group>
              </>
            )}
          </Modal.Body>
          
          <Modal.Footer className="border-0 pt-0 pb-4 px-4 justify-content-center">
            <Button 
              variant="outline-secondary" 
              onClick={() => setShowPaiementModal(false)}
              className="px-4 py-2 rounded-pill"
              style={{ minWidth: '120px' }}
            >
              <i className="ri-close-line me-2"></i>
              Annuler
            </Button>
            <Button 
              variant="success" 
              type="submit" 
              className="px-4 py-2 rounded-pill"
              style={{ 
                minWidth: '180px',
                background: 'linear-gradient(135deg, #10b981, #059669)',
                border: 'none'
              }}
            >
              <i className="ri-check-line me-2"></i>
              Enregistrer le paiement
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Modal d'historique des paiements */}
      <Modal show={showHistoriqueModal} onHide={() => setShowHistoriqueModal(false)} size="lg" centered className="modern-modal">
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="w-100">
            <div className="text-center">
              <div className="modal-icon mb-3">
                <div className="icon-wrapper bg-info-soft">
                  <i className="ri-history-line text-info" style={{ fontSize: '2.5rem' }}></i>
                </div>
              </div>
              <h3 className="fw-bold text-info">Historique des paiements</h3>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-2 px-4">
          {selectedFacture && (
            <>
              <Alert variant="light" className="mb-3 p-3" style={{
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                border: 'none'
              }}>
                <div className="d-flex justify-content-between">
                  <div>
                    <strong>Facture {selectedFacture.numero_facture}</strong>
                    <div className="small text-muted">Client: {selectedFacture.client}</div>
                  </div>
                  <div className="text-end">
                    <span className="badge bg-primary">Total: {formatCurrency(selectedFacture.montant_ttc)}</span>
                  </div>
                </div>
              </Alert>

              {selectedFacture.paiements?.length > 0 ? (
                <Table striped hover className="align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>Date</th>
                      <th>N° Opération</th>
                      <th>Mode</th>
                      <th>Référence</th>
                      <th className="text-end">Montant</th>
                      <th>Statut</th>
                      <th>Saisi par</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedFacture.paiements.map((paiement, idx) => (
                      <tr key={idx}>
                        <td>{formatDate(paiement.date_reglement)}</td>
                        <td>{paiement.numero_reglement}</td>
                        <td>
                          <i className={`${getModePaiementIcon(paiement.mode_reglement)} me-2`}></i>
                          {paiement.mode_reglement}
                        </td>
                        <td>{paiement.reference || '-'}</td>
                        <td className="text-end fw-bold text-success">{formatCurrency(paiement.montant)}</td>
                        <td>
                          <Badge bg={paiement.statut === 'valide' ? 'success' : 'warning'}>
                            {paiement.statut}
                          </Badge>
                        </td>
                        <td>{paiement.prenom_utilisateur} {paiement.nom_utilisateur}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="table-light">
                    <tr className="fw-bold">
                      <td colSpan="4" className="text-end">TOTAL PAYÉ:</td>
                      <td className="text-end text-success">
                        {formatCurrency(selectedFacture.paiements.reduce((acc, p) => acc + p.montant, 0))}
                      </td>
                      <td colSpan="2"></td>
                    </tr>
                  </tfoot>
                </Table>
              ) : (
                <div className="text-center py-5">
                  <i className="ri-inbox-line fs-48 text-muted mb-3"></i>
                  <p className="text-muted">Aucun paiement enregistré pour cette facture</p>
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0 pb-4 justify-content-center">
          <Button variant="secondary" onClick={() => setShowHistoriqueModal(false)} className="px-5 rounded-pill">
            Fermer
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal d'envoi de relance */}
<Modal show={showRelanceModal} onHide={() => setShowRelanceModal(false)} centered className="modern-modal">
  <Modal.Header closeButton className="border-0 pb-0">
    <Modal.Title className="w-100">
      <div className="text-center">
        <div className="modal-icon mb-3">
          <div className="icon-wrapper bg-warning-soft">
            <i className="ri-mail-send-line text-warning" style={{ fontSize: '2.5rem' }}></i>
          </div>
        </div>
        <h3 className="fw-bold text-warning">Envoyer une relance</h3>
        <p className="text-white small">Choisissez le type de relance</p>
      </div>
    </Modal.Title>
  </Modal.Header>
  
  <Modal.Body className="pt-2 px-4">
    {selectedFacture && (
      <>
       <Alert variant="light" className="mb-4 p-4" style={{
  borderRadius: '20px',
  background: 'linear-gradient(135deg, #ffffff, #f8f9fa)',
  border: '1px solid rgba(0,0,0,0.05)',
  boxShadow: '0 8px 20px rgba(0,0,0,0.02)'
}}>
  <Row className="align-items-center g-3">
    <Col xs={12} md={6}>
      <div className="d-flex align-items-center gap-3">
        <div className="bg-primary bg-opacity-10 p-3 rounded-3">
          <i className="ri-file-list-3-line text-primary fs-4"></i>
        </div>
        <div>
          <h6 className="mb-1 fw-bold">{selectedFacture.numero_facture}</h6>
          <p className="mb-0 text-muted small">
            <i className="ri-user-line me-1"></i>
            {selectedFacture.client}
          </p>
        </div>
      </div>
    </Col>
    <Col xs={12} md={6}>
      <div className="text-md-end">
        <span className="badge bg-danger px-4 py-3 rounded-pill fs-7">
          <i className="ri-money-euro-circle-line me-2"></i>
          Reste: {formatCurrency(selectedFacture.reste_a_payer)}
        </span>
      </div>
    </Col>
  </Row>
</Alert>

        <Form.Group className="mb-4" style={{display:'none'}}>
          <Form.Label className="fw-semibold">
            <i className="ri-mail-send-line me-2 text-warning"></i>
            Type de relance
          </Form.Label>
          <Form.Select
            value={relanceData.type}
            onChange={(e) => setRelanceData({...relanceData, type: e.target.value})}
            className="form-select-modern py-2"
          >
            <option value="standard">Standard</option>
            <option value="premiere">Première relance</option>
            <option value="deuxieme">Deuxième relance - Urgent</option>
            <option value="derniere">Dernière relance - Mise en demeure</option>
          </Form.Select>
          <Form.Text className="text-muted">
            Le message sera adapté automatiquement selon le type de relance
          </Form.Text>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label className="fw-semibold">
            <i className="ri-chat-1-line me-2 text-primary"></i>
            Message personnalisé (optionnel)
          </Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={relanceData.message_personnalise}
            onChange={(e) => setRelanceData({...relanceData, message_personnalise: e.target.value})}
            placeholder="Ajoutez un message personnalisé si nécessaire..."
            className="form-control-modern"
          />
        </Form.Group>
      </>
    )}
  </Modal.Body>
  
  <Modal.Footer className="border-0 pt-0 pb-4 px-4 justify-content-center">
    <Button 
      variant="outline-secondary" 
      onClick={() => setShowRelanceModal(false)}
      className="px-4 py-2 rounded-pill"
      style={{ minWidth: '120px' }}
    >
      <i className="ri-close-line me-2"></i>
      Annuler
    </Button>
    <Button 
      variant="warning" 
      onClick={handleConfirmerRelance}
      disabled={sendingRelance}
      className="px-4 py-2 rounded-pill"
      style={{ 
        minWidth: '180px',
        background: 'linear-gradient(135deg, #f59e0b, #d97706)',
        border: 'none'
      }}
    >
      {sendingRelance ? (
        <>
          <Spinner size="sm" className="me-2" />
          Envoi...
        </>
      ) : (
        <>
          <i className="ri-mail-send-line me-2"></i>
          Envoyer la relance
        </>
      )}
    </Button>
  </Modal.Footer>
</Modal>

      {/* Styles pour les notifications */}
      <style>{`
        .notification-container {
          position: fixed;
          top: 20px;
          right: 20px;
          z-index: 9999;
          display: flex;
          flex-direction: column;
          gap: 10px;
          pointer-events: none;
        }

        .notification {
          position: relative;
          min-width: 350px;
          max-width: 450px;
          padding: 16px 20px;
          background: white;
          border-radius: 16px;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
          display: flex;
          align-items: center;
          gap: 15px;
          transform: translateX(120%);
          transition: transform 0.3s ease, opacity 0.3s ease;
          opacity: 0;
          pointer-events: all;
          border-left: 4px solid;
          overflow: hidden;
        }

        .notification.show {
          transform: translateX(0);
          opacity: 1;
        }

        .notification-success {
          border-left-color: #10b981;
        }

        .notification-success .notification-icon {
          background: linear-gradient(135deg, #10b981, #059669);
        }

        .notification-error {
          border-left-color: #ef4444;
        }

        .notification-error .notification-icon {
          background: linear-gradient(135deg, #ef4444, #dc2626);
        }

        .notification-icon {
          width: 45px;
          height: 45px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 24px;
          flex-shrink: 0;
          box-shadow: 0 5px 15px rgba(0,0,0,0.2);
        }

        .notification-content {
          flex: 1;
        }

        .notification-title {
          margin: 0 0 4px 0;
          font-size: 1rem;
          font-weight: 600;
          color: #1e293b;
        }

        .notification-message {
          margin: 0;
          font-size: 0.9rem;
          color: #64748b;
        }

        .notification-close {
          background: transparent;
          border: none;
          color: #94a3b8;
          font-size: 18px;
          cursor: pointer;
          padding: 5px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: color 0.2s ease;
          position: relative;
          z-index: 2;
        }

        .notification-close:hover {
          color: #1e293b;
        }

        .notification-progress {
          position: absolute;
          bottom: 0;
          left: 0;
          height: 3px;
          width: 100%;
          background: rgba(0, 0, 0, 0.1);
          animation: progress 4s linear forwards;
        }

        .notification-success .notification-progress {
          background: linear-gradient(90deg, #10b981, #059669);
        }

        .notification-error .notification-progress {
          background: linear-gradient(90deg, #ef4444, #dc2626);
        }

        @keyframes progress {
          0% { width: 100%; }
          100% { width: 0%; }
        }

        @media (max-width: 768px) {
          .notification-container {
            top: 10px;
            right: 10px;
            left: 10px;
          }
          
          .notification {
            min-width: auto;
            width: 100%;
            max-width: none;
          }
        }

        /* Vos autres styles */
        .bg-warning-soft {
          background: rgba(245, 158, 11, 0.15);
        }
        
        .action-btn {
          transition: all 0.3s ease;
          border: 1px solid #e2e8f0;
          background: white;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }

        .action-btn:hover {
          transform: translateY(-3px);
          box-shadow: 0 4px 8px rgba(0,0,0,0.1);
        }

        .action-btn.btn-outline-primary:hover {
          background: linear-gradient(135deg, #4361ee, #3a56d4);
          border-color: #4361ee;
          color: white;
        }

        .action-btn.btn-outline-success:hover {
          background: linear-gradient(135deg, #10b981, #059669);
          border-color: #10b981;
          color: white;
        }

        .action-btn.btn-outline-warning:hover {
          background: linear-gradient(135deg, #f59e0b, #d97706);
          border-color: #f59e0b;
          color: white;
        }

        .modern-modal .modal-content {
          border: none;
          border-radius: 24px;
          box-shadow: 0 20px 40px rgba(0,0,0,0.2);
          overflow: hidden;
        }

        .icon-wrapper {
          width: 80px;
          height: 80px;
          margin: 0 auto;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          animation: pulse 2s infinite;
        }

        .bg-success-soft {
          background: rgba(16, 185, 129, 0.15);
        }

        .bg-info-soft {
          background: rgba(59, 130, 246, 0.15);
        }

        @keyframes pulse {
          0% { transform: scale(1); }
          50% { transform: scale(1.05); }
          100% { transform: scale(1); }
        }

        .form-control-modern {
          border: 2px solid #e2e8f0;
          border-radius: 12px;
          padding: 0.6rem 1rem;
          transition: all 0.3s ease;
        }

        .form-control-modern:focus {
          border-color: #4361ee;
          box-shadow: 0 0 0 3px rgba(67, 97, 238, 0.15);
          outline: none;
        }

        .form-select-modern {
          border: 2px solid #e2e8f0;
          border-radius: 12px;
          padding: 0.6rem 1rem;
          transition: all 0.3s ease;
          cursor: pointer;
        }

        .form-select-modern:focus {
          border-color: #4361ee;
          box-shadow: 0 0 0 3px rgba(67, 97, 238, 0.15);
          outline: none;
        }
      `}</style>
    </React.Fragment>
  );
}